import { loadStripe, Stripe } from "@stripe/stripe-js";

class StripeService {
	private static instance: StripeService;
	private stripe: Promise<Stripe | null>;

	private constructor() {
		// Initialize Stripe with your publishable key
		const publishableKey =
			process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

		if (!publishableKey) {
		}

		this.stripe = loadStripe(publishableKey);
	}

	/**
	 * Utility method to perform API calls with retry logic and exponential backoff
	 */
	private async apiCallWithRetry<T>(
		fn: () => Promise<T>,
		maxRetries: number = 3,
		baseDelay: number = 1000,
	): Promise<T> {
		let lastError: Error = new Error("Unknown error");

		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			try {
				return await fn();
			} catch (error: any) {
				lastError = error;

				// Don't retry 4xx errors (client errors) except 429 (rate limit)
				if (
					error.status >= 400 &&
					error.status < 500 &&
					error.status !== 429
				) {
					throw error;
				}

				if (attempt < maxRetries) {
					// Exponential backoff with jitter
					const delay =
						baseDelay * Math.pow(2, attempt) + Math.random() * 1000;

					await new Promise((resolve) => setTimeout(resolve, delay));
				}
			}
		}

		throw lastError;
	}

	public static getInstance(): StripeService {
		if (!StripeService.instance) {
			StripeService.instance = new StripeService();
		}
		return StripeService.instance;
	}

	public async getStripe(): Promise<Stripe | null> {
		return await this.stripe;
	}

	public async createCheckoutSession(
		priceId: string,
		customerId?: string,
		accessToken?: string,
		options?: { successUrl?: string; cancelUrl?: string },
	): Promise<{ sessionId: string }> {
		return this.apiCallWithRetry(async () => {
			const token =
				accessToken || (await window.appContexto?.getAccessToken());

			const successUrl =
				options?.successUrl ||
				`${window.location.origin}/dashboard?payment=success&session_id={CHECKOUT_SESSION_ID}`;
			const cancelUrl =
				options?.cancelUrl ||
				`${window.location.origin}/dashboard?payment=cancelled`;

			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/create-checkout-session`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						customerId,
						priceId,
						successUrl,
						cancelUrl,
					}),
				},
			);

			if (!response.ok) {
				let errorMessage = `Failed to create checkout session: ${response.statusText}`;
				try {
					const errorData = await response.json();
					console.error("API Error Response:", errorData);
					errorMessage =
						errorData.message || errorData.error || errorMessage;
				} catch (e) {
					console.error("Could not parse error response");
				}
				const error = new Error(errorMessage);
				(error as any).status = response.status;
				throw error;
			}

			const data = await response.json();

			// Handle both response formats for compatibility
			// New format: {success: true, sessionId: ..., url: ...}
			// Old format: {Ok: true, Data: {sessionId: ...}}
			if (data.success && data.sessionId) {
				return { sessionId: data.sessionId };
			} else if (data.Ok && data.Data) {
				const { sessionId } = data.Data;
				return { sessionId };
			} else {
				throw new Error("Invalid response format from checkout API");
			}
		});
	}

	public async redirectToCheckout(sessionId: string): Promise<void> {
		const stripe = await this.getStripe();

		if (!stripe) {
			throw new Error("Stripe not initialized");
		}

		const { error } = await stripe.redirectToCheckout({ sessionId });

		if (error) {
			console.error("Error redirecting to checkout:", error);
			throw error;
		}
	}

	public async createPortalSession(): Promise<{ url: string }> {
		return this.apiCallWithRetry(async () => {
			// Ensure we have a valid token
			const token = await window.appContexto?.getAccessToken();

			if (!token) {
				throw new Error(
					"Authentication token not available. Please log in again.",
				);
			}

			// Use the subscription/manage-url endpoint which properly handles customer ID lookup
			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/manage-url`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				let errorMessage = `Failed to create portal session: ${response.statusText}`;
				try {
					const errorData = await response.json();
					console.error("Portal session error response:", errorData);
					errorMessage =
						errorData.message || errorData.error || errorMessage;
				} catch (e) {
					console.error("Could not parse error response");
				}
				const error = new Error(errorMessage);
				(error as any).status = response.status;
				throw error;
			}

			const data = await response.json();

			// Handle different response formats
			if (data.url) {
				return { url: data.url };
			} else if (data.Data && data.Data.url) {
				return { url: data.Data.url };
			} else {
				throw new Error("Invalid response format: missing URL");
			}
		});
	}

	public async getSubscriptionStatus(customerId: string): Promise<{
		status: string;
		currentPeriodEnd: number;
		priceId: string;
	}> {
		return this.apiCallWithRetry(async () => {
			const token = await window.appContexto.getAccessToken();

			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/customer-status/${customerId}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				const error = new Error(
					`Failed to get subscription status: ${response.statusText}`,
				);
				(error as any).status = response.status;
				throw error;
			}

			return await response.json();
		});
	}

	public async cancelSubscription(subscriptionId: string): Promise<void> {
		try {
			const token = await window.appContexto.getAccessToken();

			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/cancel`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ subscriptionId }),
				},
			);

			if (!response.ok) {
				throw new Error("Failed to cancel subscription");
			}
		} catch (error) {
			console.error("Error canceling subscription:", error);
			throw error;
		}
	}

	public async updateSubscription(
		_subscriptionId: string,
		_newPriceId: string,
	): Promise<void> {
		try {
			// For now, just redirect to customer portal for updates
			// Later we can implement direct subscription updates
			const portalSession = await this.createPortalSession();
			window.location.href = portalSession.url;
		} catch (error) {
			console.error("Error updating subscription:", error);
			throw error;
		}
	}

	/**
	 * Get checkout session status and subscription details
	 * @param sessionId The checkout session ID from Stripe
	 */
	public async getCheckoutSessionStatus(sessionId: string): Promise<{
		status: string;
		subscriptionId?: string;
		customerId?: string;
		subscriptionStatus?: string;
		currentPlan?: string;
		currentPeriodEnd?: number;
		priceId?: string;
		paymentStatus?: string;
		message?: string;
	}> {
		const token = await window.appContexto.getAccessToken();
		if (!token) {
			throw new Error("No authentication token available");
		}

		const response = await fetch(
			`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/session-status?sessionId=${sessionId}`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			},
		);

		if (!response.ok) {
			const errorData = await response.json().catch(() => null);
			throw new Error(
				errorData?.message || "Failed to get session status",
			);
		}

		return response.json();
	}
}

export default StripeService;
